import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { List, Map } from 'immutable';
import { selectOptions } from '../ActiveGame/selectors';
import { optionReason, sourceOption } from '../ActiveGame/options';
import ConfirmButton from '../ConfirmButton';
import { Button, Muted } from '../Controls';
import PickSteps from '../Picks/PickSteps';
import { startPicks, submitPicks } from '../Picks/actions';
import { POWER } from '../Picks/constants';
import { selectPicksSource, selectSubmittingPicks } from '../Picks/selectors';

/**
 * A Pokémon's Powers that are used during the turn (Rain Dance, Damage Swap), each a button greyed out with the
 * server's reason when it can't be used now. One that picks nothing (Energy Burn) asks to confirm with its text;
 * the rest go step by step like a Trainer. Powers that just happen (Strikes Back) have no button: the server sends
 * no option for them.
 */
const PowerButtons = ({ card, at }) => {
  const dispatch = useDispatch();
  const options = useSelector(selectOptions);
  const picksSource = useSelector(selectPicksSource);
  const submitting = useSelector(selectSubmittingPicks);

  const powers = (card.get('moves') || List()).filter(move => move.get('is_power'));

  return powers.map((move) => {
    const name = move.get('name');
    const source = Map({ kind: POWER, at, moveId: move.get('id'), name });
    const option = sourceOption(options, source);

    if (option === undefined) {
      return null;
    }

    if (picksSource && picksSource.get('kind') === POWER && picksSource.get('at') === at
      && picksSource.get('moveId') === move.get('id')) {
      return <PickSteps key={name} />;
    }

    if (option === true) {
      return (
        <ConfirmButton
          key={name}
          onConfirm={() => dispatch(submitPicks(source))}
          disabled={submitting}
          prompt={`Use ${name}? ${move.get('text') || ''}`.trim()}
          primary
        >
          Use {name}
        </ConfirmButton>
      );
    }

    const reason = typeof option === 'string' ? option : null;

    return (
      <React.Fragment key={name}>
        <Button
          $primary
          onClick={() => dispatch(startPicks(source, move.get('power_steps')))}
          disabled={!!reason || submitting}
        >
          Use {name}
        </Button>
        {reason && <Muted>{optionReason(reason)}</Muted>}
      </React.Fragment>
    );
  });
};

PowerButtons.propTypes = {
  /** The Pokémon's card, with its moves. */
  card: PropTypes.instanceOf(Map).isRequired,
  /** Where the Pokémon is: 'active' or a bench position. */
  at: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default PowerButtons;
